// @flow
import type {AbstractComponent, Node} from 'react'
import React, {Component} from 'react'
import {createFragmentContainer, graphql} from 'react-relay'
import type {$RelayProps} from 'react-relay'
import {debounce} from 'underscore'
import {pathOr} from 'ramda'

import BoltSolid from 'components/ui/icons/fa/BoltSolid'
import TensionBoxContainer from 'components/tensions/ui/TensionBoxContainer'
import UpdateTensionMutation from 'mutations/UpdateTensionMutation'
import FormGroup from 'components/ui/FormGroup'

import SelectRole from './SelectRole'
import styles from './index.scss'
import type {
  TensionEditor_proposal as Proposal,
} from './__generated__/TensionEditor_proposal.graphql'
import type {
  TensionEditor_meeting as Meeting,
} from './__generated__/TensionEditor_meeting.graphql'
import type {
  TensionEditor_tension as Tension,
} from './__generated__/TensionEditor_tension.graphql'
import type {
  TensionEditor_organization as Organization,
} from './__generated__/TensionEditor_organization.graphql'
import TensionBodyInput from './TensionBodyInput'

type Props = {
  isReadonly: boolean,
  meeting: ?Meeting,
  organization: Organization,
  proposal: Proposal,
  tension: Tension,
  updateOnUnmount: boolean,
  providedTension?: ?string,
}

type State = {
  impactedRoleId: ?string,
  body: string,
}

const meetingId: (?Meeting) => ?string = pathOr(null, ['id'])
const impactedRoleId: (Tension) => ?string = pathOr(null, ['impactedRole', 'id'])
const tensionBody: (Tension) => string = pathOr('', ['body'])

const isEditing = () => document.hasFocus()

const stateFromProps = (props: Props) => ({
  body: tensionBody(props.tension),
  impactedRoleId: impactedRoleId(props.tension),
})

class TensionEditor extends Component<Props, State> {
  static defaultProps: { updateOnUnmount: boolean } = {
    updateOnUnmount: true,
  }

  // If the viewer is the host, treat local state as authoritative and
  // don't update it out from under the host.  If the viewer is not the
  // host, update local state from the props in order to show the
  // latest changes.
  static getDerivedStateFromProps(props: Props, state: State): null | { body: string, impactedRoleId: ?string } {
    if (isEditing())
      return null

    return {
      ...state,
      ...stateFromProps(props),
    }
  }

  constructor(props: Props) {
    super(props)
    this.mutationCanceled = false
    this.state = stateFromProps(props)
    this.debouncedMutate = debounce(this.updateTension, 500)
  }

  componentDidMount() {
    if (this.props.providedTension)
      this.onBodyChange({target: {value: this.props.providedTension}})
  }

  componentWillUnmount() {
    if (this.props.isReadonly)
      return
    this.mutationCanceled = true
    if (this.props.updateOnUnmount && !this.props.tension.isAutogenerated)
      this.reallyUpdateTension()
  }

  onBodyChange: ((
      event: SyntheticInputEvent<HTMLInputElement> | {target: {value: string}}) => void
      ) = (event: SyntheticInputEvent<HTMLInputElement> | {target: {value: string}}) => {
      this.setState({body: event.target.value || ''}, this.debouncedMutate)
    }

  onRoleChange: ((option: { value: string, ... }) => void) = (option: {value: string, ...}) => (
    this.setState({impactedRoleId: option ? option.value : null}, this.debouncedMutate)
  )

  get isMeeting(): boolean {
    return !!this.props.meeting
  }

  reallyUpdateTension: (() => void) = (): void => {
    UpdateTensionMutation(
      this.props.tension.id,
      meetingId(this.props.meeting),
      this.state,
    )
  }

  updateTension: (() => null | void) = (): null | void => (this.mutationCanceled ? null : this.reallyUpdateTension())

  debouncedMutate: Function
  mutationCanceled: boolean

  render(): Node {
    return (
      <TensionBoxContainer marginBottom="MEDIUM" test-id="proposal-tension">
        <div className={styles.container}>
          <div className={styles.boltContainer}>
            <span className={styles.tension}>
              {I18n.t('meetings.react.tension')}
            </span>
            <BoltSolid className={styles.boltIcon} />
          </div>
          <div className={styles.form}>
            <TensionBodyInput
              circle={this.props.proposal.circle}
              organization={this.props.organization}
              onChange={this.onBodyChange}
              value={this.state.body}
              isReadonly={this.props.isReadonly}
            />
            <FormGroup
              label={I18n.t('tensions.react.labels.role')}
              target="impacts-role"
              hideRightButton
            >
              <SelectRole
                isReadonly={this.props.isReadonly}
                isMeeting={this.isMeeting}
                onChange={this.onRoleChange}
                tension={this.props.tension}
                value={this.state.impactedRoleId}
              />
            </FormGroup>
          </div>
        </div>
      </TensionBoxContainer>
    )
  }
}

export default (createFragmentContainer(TensionEditor, {
  meeting: graphql`
    fragment TensionEditor_meeting on Meeting {
      id
    }
  `,
  organization: graphql`
    fragment TensionEditor_organization on Organization {
      databaseId
      
      ...TensionBodyInput_organization
    }
  `,
  tension: graphql`
    fragment TensionEditor_tension on Tension {
      body
      databaseId
      id
      isAutogenerated

      impactedRole {
        id
      }

      ...SelectRole_tension
    }
  `,
  proposal: graphql`
    fragment TensionEditor_proposal on Proposal {
      circle {
        databaseId
        
        ...TensionBodyInput_circle
      }
    }
  `,
}): AbstractComponent<
  $RelayProps<React$ElementConfig<typeof TensionEditor>>,
  TensionEditor,
>)
